import React from 'react'
import { AlertTriangle } from 'lucide-react'
import { toast } from 'react-hot-toast'

/**
 * GlobalErrorBoundary – Top-level Error Boundary voor de Orion applicatie
 * Vangt alle niet-afgehandelde React-fouten op en toont een gebruiksvriendelijke
 * Nederlandse fallback UI in plaats van een witte scherm.
 * Vangt ook unhandledrejection (o.a. AxiosErrors) en toont toast i.p.v. console-spam.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    const err = event?.reason
    if (!err || event.defaultPrevented) return

    // AxiosErrors: toon vriendelijke toast en voorkom dubbele console-output
    if (err?.name === 'AxiosError') {
      event.preventDefault()
      const status = err?.response?.status
      const detail = err?.response?.data?.detail
      let msg = typeof detail === 'string' ? detail : null
      if (Array.isArray(detail)) {
        msg = detail.map((d: { msg?: string; loc?: string[] }) => d?.msg || d?.loc?.join('.')).filter(Boolean).join('; ')
      }
      if (!msg) {
        if (status === 422) msg = 'Ongeldige gegevens. Controleer het formulier.'
        else if (status === 500) msg = 'Serverfout. Probeer het later opnieuw.'
        else if (!err?.response) msg = 'Geen verbinding met de server.'
        else msg = err?.message || 'Er is een fout opgetreden.'
      }
      toast.error(msg, { duration: 6000 })
      if (process.env.NODE_ENV === 'development') {
        console.error('[GlobalErrorBoundary] Unhandled AxiosError:', err?.config?.url, err?.response?.status, err)
      }
      return
    }

    // Overige rejections: alleen loggen
    console.error('[GlobalErrorBoundary] Unhandled promise rejection:', err)
  })
}
interface GlobalErrorBoundaryProps {
  children: React.ReactNode
}

interface GlobalErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
}

class GlobalErrorBoundary extends React.Component<
  GlobalErrorBoundaryProps,
  GlobalErrorBoundaryState
> {
  constructor(props: GlobalErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<GlobalErrorBoundaryState> {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('[GlobalErrorBoundary] Onverwachte fout opgevangen:', error, errorInfo)
    this.setState({ error, errorInfo })
  }

  handleReload = (): void => {
    window.location.reload()
  }

  handleGoHome = (): void => {
    window.location.href = '/'
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 bg-red-100 dark:bg-red-900/30 rounded-full">
              <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>

            <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-3">
              Er is een onverwachte fout opgetreden
            </h2>

            <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
              Er is een onverwachte fout opgetreden. Herlaad de pagina.
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mb-6 p-4 bg-gray-100 dark:bg-gray-700/50 rounded-lg text-xs">
                <summary className="cursor-pointer font-semibold mb-2 text-gray-700 dark:text-gray-300">
                  Technische details (alleen in development)
                </summary>
                <pre className="overflow-auto max-h-48 text-red-600 dark:text-red-400 whitespace-pre-wrap">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div className="flex gap-3">
              <button
                onClick={this.handleReload}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Herlaad de pagina
              </button>
              <button
                onClick={this.handleGoHome}
                className="flex-1 px-4 py-3 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 rounded-lg font-medium transition-colors"
              >
                Naar startpagina
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default GlobalErrorBoundary
